import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Milestone, isCalendarDay, sortMilestones } from '@the-real-insight/tri-model';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQueuedWrite } from './useQueuedWrite';

/**
 * "Projektplanung": the milestones of a project, a date and a name each.
 *
 * The list is always in time order — that is what makes it a plan rather than a list of notes. An
 * entry moves to its place when its date is committed, not while it is typed: a row that jumped
 * away under the cursor at the first digit of the year could not be edited at all.
 *
 * Every change writes the whole plan at once, like every other field of the drill-down.
 */

interface Properties {
    value: Milestone[] | null | undefined;
    onChange: (next: Milestone[]) => Promise<void>;
    disabled?: boolean;
}

const newId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Today as `YYYY-MM-DD` in the reader's own time zone — the day they would call today. */
const today = (): string => {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export default function MilestonePlan({ value, onChange, disabled }: Properties) {
    const { t } = useTranslation();
    const stored = useMemo(() => sortMilestones(value ?? []), [value]);
    const describeError = useCallback((error: any) => error?.message ?? t('global.generalError'), [t]);
    const { value: milestones, apply, error } = useQueuedWrite(stored, onChange, describeError);
    /** What is typed into an existing row but not yet left. */
    const [edits, setEdits] = useState<Record<string, Partial<Milestone>>>({});
    const [draft, setDraft] = useState<{ date: string; name: string }>({ date: '', name: '' });

    const current = today();
    // The next milestone is the first one not in the past; everything before it is done with.
    const nextId = milestones.find((milestone) => milestone.date >= current)?.id;

    const write = (next: Milestone[]) => apply(sortMilestones(next));

    const change = (id: string, patch: Partial<Milestone>) =>
        setEdits((all) => ({ ...all, [id]: { ...all[id], ...patch } }));

    /** A row was left: write what changed, provided it is still a milestone. */
    const commit = (milestone: Milestone) => {
        const patch = edits[milestone.id];
        setEdits((all) => {
            const rest = { ...all };
            delete rest[milestone.id];
            return rest;
        });
        if (!patch) return;

        const next = { ...milestone, ...patch, name: (patch.name ?? milestone.name).trim() };
        // An emptied name or a half-typed date is not saved; the row falls back to what is stored.
        if (!next.name || !isCalendarDay(next.date)) return;
        if (next.name === milestone.name && next.date === milestone.date) return;

        write(milestones.map((entry) => (entry.id === milestone.id ? next : entry)));
    };

    const add = () => {
        const name = draft.name.trim();
        if (!name || !isCalendarDay(draft.date)) return;

        write([...milestones, { id: newId(), date: draft.date, name }]);
        setDraft({ date: '', name: '' });
    };

    const canAdd = !!draft.name.trim() && isCalendarDay(draft.date);

    return (
        <div>
            {milestones.length === 0 ? (
                <div className="mb-3 text-sm text-muted-foreground">{t('portfolioDashboard.milestones.empty')}</div>
            ) : (
                <ol className="m-0 mb-3 flex list-none flex-col p-0">
                    {milestones.map((milestone) => {
                        const edit = edits[milestone.id] ?? {};
                        const past = milestone.date < current;

                        return (
                            <li
                                key={milestone.id}
                                className={cn(
                                    'grid grid-cols-[9.5rem_minmax(0,1fr)_auto] items-center gap-2 border-b py-1.5',
                                    past && 'text-muted-foreground',
                                )}
                                // Leaving the row, not the field: moving from date to name is not
                                // yet a commit, or the row would re-sort between the two.
                                onBlur={(event) => {
                                    if (!event.currentTarget.contains(event.relatedTarget as globalThis.Node | null)) {
                                        commit(milestone);
                                    }
                                }}
                            >
                                <Input
                                    type="date"
                                    value={edit.date ?? milestone.date}
                                    disabled={disabled}
                                    aria-label={t('portfolioDashboard.milestones.date')}
                                    className={cn('h-8 tabular-nums', past && 'text-muted-foreground')}
                                    onChange={(event) => change(milestone.id, { date: event.target.value })}
                                />
                                <div className="flex min-w-0 items-center gap-2">
                                    <Input
                                        value={edit.name ?? milestone.name}
                                        disabled={disabled}
                                        aria-label={t('portfolioDashboard.milestones.name')}
                                        className={cn('h-8', past && 'text-muted-foreground')}
                                        onChange={(event) => change(milestone.id, { name: event.target.value })}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                                        }}
                                    />
                                    {milestone.id === nextId ? (
                                        <Badge variant="secondary" className="shrink-0">
                                            {t('portfolioDashboard.milestones.next')}
                                        </Badge>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                    disabled={disabled}
                                    aria-label={t('portfolioDashboard.milestones.remove', { name: milestone.name })}
                                    onClick={() => write(milestones.filter((entry) => entry.id !== milestone.id))}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            </li>
                        );
                    })}
                </ol>
            )}

            {disabled ? (
                <></>
            ) : (
                <form
                    className="grid grid-cols-[9.5rem_minmax(0,1fr)_auto] items-center gap-2"
                    onSubmit={(event) => {
                        event.preventDefault();
                        add();
                    }}
                >
                    <Input
                        type="date"
                        value={draft.date}
                        aria-label={t('portfolioDashboard.milestones.newDate')}
                        className="h-8 tabular-nums"
                        onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value }))}
                    />
                    <Input
                        value={draft.name}
                        placeholder={t('portfolioDashboard.milestones.namePlaceholder')}
                        aria-label={t('portfolioDashboard.milestones.newName')}
                        className="h-8"
                        onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                    />
                    <Button type="submit" variant="outline" size="sm" className="h-8 gap-1.5" disabled={!canAdd}>
                        <Plus size={14} />
                        {t('portfolioDashboard.milestones.add')}
                    </Button>
                </form>
            )}

            {error ? (
                <Alert variant="destructive" className="mt-3">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ) : (
                <></>
            )}
        </div>
    );
}
